import { useContext, useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { IoMdAdd } from "react-icons/io";
import TrainerContext from "../../../../../context/trainerContext/TrainerContext";
import DataTableBase from "../../../common/Datatable";
import styles from "./Listing.module.scss";
import TrainerSlotsAddModal from "../AddModal";
import TrainerSlotsEditModal from "../EditModal";
import DateRangeComponent from "../../../common/DateRangeComponent";
import makeRequest from "../../../../../api";
import {
  addTrainerSlots,
  deleteTrainerSlots,
  editTrainerSlots,
  getTrainerSlotsList,
} from "../../../../../api/services/slot-management";
import { trainerService } from "../../../../../api/services";

const TrainerSlotsListing = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const currentTrainerId = queryParams.get("trainerId") || undefined;
  const date = moment();

  const { getTrainerState } = useContext(TrainerContext);
  const trainerData = getTrainerState();

  const [currentDate, setCurrentDate] = useState(date);
  const [slotsList, setSlotsList] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [showEditSlotModal, setShowEditSlotModal] = useState(false);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [trainerDetails, setTrainerDetails] = useState<any>(null);

  const startDate = moment(currentDate)
    .startOf("week")
    .add(1, "day")
    .format("YYYY-MM-DD");
  const endDate = moment(currentDate)
    .endOf("week")
    .add(1, "day")
    .format("YYYY-MM-DD");
  const { trainerId, trainerIsAdmin } = trainerData;
  const trainerIdForData =
    currentTrainerId && trainerIsAdmin ? currentTrainerId : trainerId;

  useEffect(() => {
    getSlotsList();
  }, [currentDate]);

  useEffect(() => {
    if (!!currentTrainerId) {
      getTrainerDetails(currentTrainerId);
    }
  }, [currentTrainerId]);

  const getTrainerDetails = async (currentTrainerId: any) => {
    const res = await makeRequest(
      trainerService.getTrainerData(
        { trainerId: currentTrainerId },
        currentTrainerId
      )
    );
    setTrainerDetails({ ...res?.data?.trainer });
  };

  const handleChangeCurrentDate = (action: string) => {
    if (action === "add") {
      setCurrentDate(moment(currentDate).add("1", "week"));
    } else {
      setCurrentDate(moment(currentDate).subtract("1", "week"));
    }
  };

  const handleEditSlotModal = () => {
    setShowEditSlotModal((prev) => !prev);
  };

  const handleAddSlotModal = () => {
    setShowAddSlotModal((prev) => !prev);
  };

  const getSlotsList = async () => {
    try {
      const response = await makeRequest(
        getTrainerSlotsList(
          {
            trainerId: trainerIdForData,
            startDate,
            endDate,
          },
          trainerIdForData
        )
      );
      if (response?.status === 200) {
        const res = response?.data?.body?.sort(
          (a: any, b: any) => +a?.startTime - +b?.startTime
        );
        const dayWiseSlots = Array(moment(endDate).diff(startDate, "days") + 1)
          .fill({ date: startDate })
          .map((item, index) => {
            const arr: any = [];
            return {
              date: moment(startDate).add(index, "day").format("YYYY-MM-DD"),
              slots: arr,
            };
          });

        res?.forEach((item: any) => {
          const date = moment(item?.startTime * 1000).format("YYYY-MM-DD");
          const dateDiff = moment(date).diff(startDate, "days");
          if (dateDiff >= 0 && dateDiff <= 6) {
            dayWiseSlots[dateDiff].slots = [
              ...dayWiseSlots[dateDiff].slots,
              {
                ...item,
                formattedStartTime: moment(item?.startTime * 1000).format(
                  "hh:mm A"
                ),
                formattedEndTime: moment(item?.endTime * 1000).format(
                  "hh:mm A"
                ),
              },
            ];
          }
        });

        const data = dayWiseSlots?.filter((item) => item?.slots?.length > 0);
        setSlotsList([...data]);
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const updateSlots = async (
    date: string,
    timings: [
      {
        [key: string]: any;
      }
    ]
  ) => {
    try {
      const response = await makeRequest(
        editTrainerSlots(
          {
            trainerId: trainerIdForData,
            date,
            timings,
          },
          trainerIdForData
        )
      );
      if (response?.status === 200) {
        getSlotsList();
        handleEditSlotModal();
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const createSlots = async (
    dates: [string],
    timings: [{ [key: string]: any }]
  ) => {
    try {
      const response = await makeRequest(
        addTrainerSlots(
          {
            trainerId: trainerIdForData,
            dates,
            timings,
          },
          trainerIdForData
        )
      );

      if (response?.status === 200) {
        getSlotsList();
        handleAddSlotModal();
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const deleteSlots = async (slotData: { [key: string]: any }) => {
    const dates = [slotData?.date];
    if (!window.confirm("Are you sure you want to delete the slot?")) {
      return;
    }

    try {
      const response = await makeRequest(
        deleteTrainerSlots(
          { trainerId: trainerIdForData, dates },
          trainerIdForData
        )
      );

      if (response?.status === 200) {
        if (response?.data?.body?.unprocessedDates?.length > 0) {
          toast.info(
            "You can't delete this time slot as you have a session scheduled."
          );
        }
        getSlotsList();
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const columns = [
    {
      name: "Date",
      selector: (row: any) => row.date,
      cell: (row: any) => {
        return (
          <div className="h-100">
            {moment(row?.date).format("ddd, DD MMM YY")}
          </div>
        );
      },
      width: "22.5%",
    },
    {
      name: "Slots",
      selector: (row: any) => row?.slots,
      cell: (row: any) => {
        return (
          <div>
            {row?.slots?.map((item: any, index: number) => (
              <div className="mb-2" key={`slot-${index}`}>
                Slot {index + 1}
              </div>
            ))}
          </div>
        );
      },
      width: "12.5%",
    },
    {
      name: "Start time",
      selector: (row: any) => row?.slots,
      cell: (row: any) => {
        return (
          <div>
            {row?.slots?.map((item: any, index: number) => (
              <div className="mb-2" key={`slot-${index}`}>
                {item?.formattedStartTime}
              </div>
            ))}
          </div>
        );
      },
      width: "12.5%",
    },
    {
      name: "End time",
      selector: (row: any) => row?.slots,
      cell: (row: any) => {
        return (
          <div>
            {row?.slots?.map((item: any, index: number) => (
              <div className="mb-2" key={`slot-${index}`}>
                {item?.formattedEndTime}
              </div>
            ))}
          </div>
        );
      },
      width: "12.5%",
    },
    {
      name: "Actions",
      selector: (row: any) => row.name,
      cell: (row: any) => {
        return (
          <div className="h-100">
            <button
              onClick={() => {
                setSelectedSlot(row);
                handleEditSlotModal();
              }}
              className="btn btn-sm btn-outline-success px-4"
            >
              Edit
            </button>
            <button
              onClick={() => {
                deleteSlots(row);
              }}
              className="btn btn-sm btn-outline-danger px-4 ms-2"
            >
              Delete
            </button>
          </div>
        );
      },
      width: "40%",
    },
  ];

  return (
    <div className={`${styles.slotsContainer} container-lg`}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h6 className="fw-700 fs-24 mb-0">
          {trainerIsAdmin ? trainerDetails?.name : "Your"} Sessions availability
        </h6>
        <DateRangeComponent
          handleDateChange={handleChangeCurrentDate}
          startDate={startDate}
          endDate={endDate}
        />
        <button
          className="btn btn-outline-success btn-sm fw-600 px-4 py-2"
          onClick={handleAddSlotModal}
        >
          <IoMdAdd className="me-1" />
          Add slots
        </button>
      </div>
      {slotsList?.length === 0 ? (
        <h6 className="mb-0">No slots defined for the week.</h6>
      ) : (
        <DataTableBase columns={columns} data={slotsList} />
      )}
      {showEditSlotModal && (
        <TrainerSlotsEditModal
          onClose={handleEditSlotModal}
          slotData={selectedSlot}
          onSave={updateSlots}
        />
      )}
      {showAddSlotModal && (
        <TrainerSlotsAddModal
          onClose={handleAddSlotModal}
          onSave={createSlots}
        />
      )}
    </div>
  );
};

export default TrainerSlotsListing;
